import type { PT } from "~/hooks/usePrismicLabel";
import type { FilledLinkToDocumentField, LinkResolverFunction } from "@prismicio/types";
import type { PrismicDocument, RichTextField } from "@prismicio/client";
import { asText } from "@prismicio/client";
import { wrapIframe } from "@commercekit/shared";
import { asHTML } from "@prismicio/helpers";

export const linkResolver = (doc: FilledLinkToDocumentField) => {
  switch (doc.type) {
    case "page":
      if (doc.uid === "home") return "/";
      if (doc.uid) return `/${doc.uid?.replace(/_/g, "/")}`;
      return "/";
    default:
      return doc.uid ? `/${doc.uid.replace(/_/g, "/")}` : `/`;
  }
};

/**
 * Resolves a RichText field to an HTML string.
 * If any paragraph node contains raw HTML tags (editors pasted raw HTML),
 * the text is used directly. Otherwise, falls back to asHTML for
 * properly structured rich text content.
 */
export const richTextResolver = (
  field: RichTextField | null | undefined,
  resolver: LinkResolverFunction
): string | undefined => {
  if (!field?.length) {
    return "";
  }

  const hasRawHtml = field.some(
    (node) =>
      node.type === "paragraph" &&
      "text" in node &&
      /<[a-zA-Z][^>]*>/.test(node.text)
  );

  if (hasRawHtml) {
    return wrapIframe(
      field.map((node) => ("text" in node ? node.text : "")).join("\n")
    );
  }

  return wrapIframe(asHTML(field, resolver)) || "";
};

export const getSeoFromDocument = (doc: PrismicDocument<{
  metaTitle: RichTextField;
  metaKeywords: RichTextField;
  metaDescription: RichTextField;
}>) => {
  const { metaTitle, metaKeywords, metaDescription } = doc.data;
  const seo = {
    title: asText(metaTitle),
    description: asText(metaDescription),
    keywords: asText(metaKeywords),
  };
  seo.description = seo.description || seo.title;
  seo.keywords = seo.keywords || seo.title;
  return seo.title ? seo : {};
};

export const formatFilterValue = (
  value: string | null | undefined,
  $pt: PT
) => {
  if (typeof value !== "string" || value?.trim() === "") {
    return "";
  }

  const lowercase = value.trim().toLowerCase();
  switch (lowercase) {
    case "true":
    case "false":
      return $pt(lowercase, undefined, true) ?? value;
    default:
      return value;
  }
};
